import { Inject, Injectable } from '@angular/core';
import { Logger } from '@utils/logger';
import { EnvConfigService } from './env-config.service';
import { DOCUMENT } from '@angular/common';
import { WINDOW } from '@ng-web-apis/common';
import { FavoritesConfig } from '@types';
import { Configuration, PageModel } from '@bloomreach/spa-sdk';
import { map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { DataDogLoggerOutput } from '@utils/logging/datadog-logger-output';
import { DatadogService } from './datadog.service';

const logger = Logger.getLogger('AppStateService');

declare var appInitChannelConfig; // appInitChannelConfig is defined in index.html

export interface ApiUrls {
  cmsBaseUrl?: string;
  spaBaseUrl: string;
  origin: string;
  endpoint?: string;
  apiVersion?: string;
}

/**
 * TODO refactor later
 * Temporary solution to cater for application state until we decide what we really want to do
 */
@Injectable({
  providedIn: 'root',
})
export class AppStateService {
  channel = 'ft-core';
  apiUrls: ApiUrls;
  token: string;
  auth: string;
  sharedCookieDomain: string;
  private debugPageModel = false;

  constructor(
    private envConfig: EnvConfigService,
    @Inject(DOCUMENT) private documentRef: Document,
    @Inject(WINDOW) readonly windowRef: Window,
    private httpClient: HttpClient,
    private datadogService: DatadogService
  ) {
    const params = new URLSearchParams(this.windowRef.location.search);
    this.token = params.get('token');
    this.auth = envConfig.getEnvConfig()?.auth;
    this.debugPageModel = params.get('debug') === 'pagemodel';

    // appInitConfig is a global variable set in the channel specific index html during the build process
    this.channel = appInitChannelConfig
      ? appInitChannelConfig.channelId
      : this.channel;
    this.sharedCookieDomain = appInitChannelConfig?.sharedCookieDomain;
    //this.initializeLogging();
  }

  // TODO: temp hacky way to work out if on home page
  isHomePage(): boolean {
    const spaBaseUrl: string = this.getApiUrls().spaBaseUrl;
    const path: string = this.windowRef.location.pathname.replace(
      spaBaseUrl,
      ''
    );
    return ['/', ''].includes(path);
  }

  /**
   * returns true if site has sign-in i.e. US initially
   * if false, we don't need to bother making accounts calls etc
   */
  hasAuthentication(): boolean {
    logger.debug('hasAuthentication()', appInitChannelConfig);
    return Boolean(appInitChannelConfig.authentication);
  }

  getAuthenticationType(): string {
    return appInitChannelConfig.authentication;
  }

  /**
   * Get the homepage url for this channel
   */
  getHomePageUrl(): string {
    logger.debug(this);
    return `${this.getApiUrls().cmsBaseUrl}`;
  }

  /**
   * Rewrites absolute home page url to relative
   * @param url - string to check
   */
  relativeHomeURL(url: string): string {
    // NGC-3177 - according to tracking requirement we need replace absolute URL to the domain to relative URL to / in logo link
    const absoluteDomainRegexp: RegExp = /^(http|https):\/\/([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9.]))+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]+(?:[:0-9]+)?(\/)?([a-z0-9_-]+)?(\/)?$/g;// NOSONAR
    if (absoluteDomainRegexp.test(url)) {
      const relativeUrl: string = url.replace(absoluteDomainRegexp, '$3$4$5');
      return relativeUrl ? relativeUrl : '/';
    }
    return url;
  }

  getChannel(): string {
    return this.channel;
  }

  getAccountsApiUrl(): string {
    if (this.getChannel() === 'en-in-new') {
      return (
        this.envConfig.getEnvConfig().ftiApiDomain +
        this.envConfig.getEnvConfig().ftiMaintenanceValidation
      );
    } else {
      //logger.error(`${this.getChannel()} does not have accounts`);
      return null;
    }
  }

  getBaseResourceApiUrl(): string {
    return `${this.getApiUrls().cmsBaseUrl}/resourceapi/`;
  }

  initializeLogging() {
    this.datadogService.initDataDog(
      this.envConfig,
      this,
      this.windowRef.location
    );
    if (this.envConfig.getEnvConfig().datadog.logsEnabled) {
      Logger.addConfiguration({
        defaultOutputs: Logger.getConfiguration().defaultOutputs.concat([
          new DataDogLoggerOutput(this.datadogService),
        ]),
      });
    }
  }

  getCookieApiUrl(): string {
    return this.envConfig.getEnvConfig().cookieApiUrl;
  }

  getPdsDefaultEnv(): string {
    return this.envConfig.getEnvConfig().defaultPds;
  }

  getPdsApiUrl(env: string): string {
    return this.envConfig.getEnvConfig()[`${env}PdsApiUrl`];
  }

  getLiteratureApiUrl(): string {
    return this.envConfig.getEnvConfig().literatureApiUrl;
  }

  getEnvironment(): string {
    return this.envConfig.getEnvConfig()?.environment;
  }

  /**
   * Load Api urls from App Config Service
   */
  getApiUrls(): ApiUrls {
    if (!this.apiUrls) {
      const envConfig = this.envConfig.getEnvConfig();
      if (envConfig) {
        const cmsPort =
          envConfig.apiPort !== undefined &&
          envConfig.apiPort !== 80 &&
          envConfig.apiPort !== 443
            ? ':' + envConfig.apiPort
            : '';
        const origin = `${envConfig.apiScheme}://${envConfig.apiUrl}${cmsPort}`;

        // allows us to pull base urls from channel or environment config if they have been set
        let spaBaseUrl =
          (appInitChannelConfig ? appInitChannelConfig.spabaseurl : false) ||
          envConfig.spabaseurl ||
          '';

        let cmsBaseUrl =
          (appInitChannelConfig ? appInitChannelConfig.cmsbaseurl : false) ||
          envConfig.cmsBaseUrl ||
          `${this.documentRef.location.origin}${spaBaseUrl}`;

        if (envConfig.singleHost) {
          spaBaseUrl = cmsBaseUrl = `/site/${this.getChannel()}`;
        }

        this.apiUrls = {
          spaBaseUrl,
          // cmsBaseUrl,
          origin,
          // remove cmsBaseUrl above and uncomment lines below to switch to v1.0 resourceApi
          apiVersion: '1.0',
          endpoint: cmsBaseUrl + '/resourceapi',
        };
      }
    }
    return this.apiUrls;
  }

  getBrConfiguration(): Configuration {
    return {
      ...this.getApiUrls(),
      httpClient: (
        ...[{data: body, headers, method, url}]: Parameters<
          Configuration['httpClient']
        >
      ) => {
        return this.httpClient
          .request<PageModel>(method, url, {
            body,
            headers: headers as Record<string, string | string[]>,
            responseType: 'json',
          })
          .pipe(map((data) => ({data})))
          .toPromise();
      },
    };
  }

  getEnvConfig() {
    return this.envConfig.getEnvConfig();
  }

  /**
   * method to get the Shared Cookie Domain
   */
  getCookieDomain(): string {
    return this.sharedCookieDomain;
  }

  /**
   * returns country local for spa, for example /en-lu
   */
  getSpaBaseUrl(): string {
    return this.apiUrls.spaBaseUrl;
  }

  getFavoritesConfig(): FavoritesConfig {
    const hasFavorites = appInitChannelConfig?.hasFavorites || false;
    const toSetFavoriteFundApiUrl = this.envConfig.getEnvConfig()
      .toSetFavoriteFundApiUrl;
    const toGetFavoriteFundApiUrl = this.envConfig.getEnvConfig()
      .toGetFavoriteFundApiUrl;
    const useLocalStorageFavoriteFunds = this.envConfig.getEnvConfig()
      .useLocalStorageFavoriteFunds;
    return {
      hasFavorites,
      loggedInOnly: appInitChannelConfig?.favouriteLoggedInOnly || false,
      useLocalStorage:
        (hasFavorites &&
          toSetFavoriteFundApiUrl &&
          toGetFavoriteFundApiUrl === undefined) ||
        useLocalStorageFavoriteFunds,
    };
  }

  // FT India Site Urls
  getPerformanceCummulativeUrl(): string {
    return (
      this.envConfig.getEnvConfig().ftiApiDomain +
      this.envConfig.getEnvConfig().perfCummulativeUrl
    );
  }

  getftiAccountsUrl(): string {
    return this.envConfig.getEnvConfig()?.ftiAccountsUrl;
  }

  getFTSearchUrl() {
    return (
      this.envConfig.getEnvConfig()?.baseUrl +
      this.envConfig.getEnvConfig().ftiSearchUrl
    );
  }

  getAutoCompleteUrl() {
    return (
      this.envConfig.getEnvConfig()?.baseUrl +
      this.envConfig.getEnvConfig().autoCompleteUrl
    );
  }

  getFtiAccountsValidateUrl() {
    return (
      this.envConfig.getEnvConfig().ftiAccountsUrl +
      this.envConfig.getEnvConfig().ftiAccountsValidate
    );
  }

  getFTIArticleListUrl() {
    return (
      this.envConfig.getEnvConfig()?.baseUrl +
      this.envConfig.getEnvConfig()?.ftiArticleList
    );
  }

  getngGuestAccountsUrl() {
    return this.envConfig.getEnvConfig().ngGuestAccountsUrl;
  }

  getBuildVersion(): string {
    return appInitChannelConfig?.buildVersion || '1.0';
  }

  isDebugPageModel(): boolean {
    return this.debugPageModel;
  }

  loadStyle(styleName: string, id: string) {
    const head = this.documentRef.getElementsByTagName('head')[0];

    const styleLink = this.documentRef.getElementById(id) as HTMLLinkElement;
    if (styleLink) {
      styleLink.href = styleName;
    } else {
      const style = this.documentRef.createElement('link');
      style.id = id;
      style.rel = 'stylesheet';
      style.href = `${styleName}`;

      head.appendChild(style);
    }
  }
}
